import { inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { confirmFn, translator } from '@softline/ui-core';
import { DeliveryNoteDocumentRepository } from '../repositories/delivery-note-document.repository';
import { deleteErrorOf, isInvoiced } from '../helper/correction-deletion.function';
import { DeliveryNoteQueryRow } from '../types/delivery-note-query-row';

const K = '#WWS_DELIVERY_NOTE_CORRECTION.DELETE';

/** Deletes a Korrekturbeleg after a confirmation; `deleted` counts the successes so the list can reload. */
@Injectable()
export class DeliveryNoteDeletionService {
  private readonly documents = inject(DeliveryNoteDocumentRepository);
  private readonly injector = inject(Injector);
  private readonly confirm = confirmFn();
  private readonly t = translator();

  readonly deleted = signal(0);
  readonly deleting = signal<string | null>(null);

  async remove(row: DeliveryNoteQueryRow): Promise<void> {
    const id = row.delivery_note?.id;
    if (!id || this.deleting() !== null || isInvoiced(row)) return;
    const answer = await this.confirm({
      title: this.t(`${K}.TITLE`),
      contentText: this.t(`${K}.TEXT`, { number: row.delivery_note.document_number }),
      showAbort: true,
    });
    if (answer !== 'YES') return;
    this.deleting.set(id);
    try {
      await this.documents.remove(id);
      this.deleted.update((count) => count + 1);
    } catch (e) {
      handleRequestError(deleteErrorOf(e), this.injector, { show: true });
    } finally {
      this.deleting.set(null);
    }
  }
}
